import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button, ThemeToggle, Breadcrumb, type BreadcrumbItem,
  CommandPalette, type CommandItem,
  NotificationBell, type Notification,
} from "@payflow/ui";
import { useAuth } from "../contexts/AuthContext";
import { useTenantTheme } from "../contexts/TenantThemeContext";
import { LanguageSelector } from "./LanguageSelector";
import { PasskeyBootstrapPrompt } from "./PasskeyBootstrapPrompt";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

type MenuLink = {
  type: "link";
  labelKey: string;
  href: string;
  icon: React.ComponentType;
  permission?: string;
  adminOnly?: boolean;
};

type MenuGroup = {
  type: "group";
  id: string;
  labelKey: string;
  icon: React.ComponentType;
  children: MenuLink[];
  adminOnly?: boolean;
};

type MenuItem = MenuLink | MenuGroup;

const menuItems: MenuItem[] = [
  {
    type: "link",
    labelKey: "nav.dashboard",
    href: "/dashboard",
    icon: HomeIcon,
  },
  {
    type: "group",
    id: "charges",
    labelKey: "nav.loans",
    icon: LoansIcon,
    children: [
      {
        type: "link",
        labelKey: "nav.loans_active",
        href: "/dashboard/emprestimos",
        icon: ActiveLoansIcon,
        adminOnly: true,
      },
      {
        type: "link",
        labelKey: "nav.pipeline",
        href: "/dashboard/pipeline",
        icon: PipelineIcon,
        adminOnly: true,
      },
      {
        type: "link",
        labelKey: "nav.renewals",
        href: "/dashboard/renovacoes",
        icon: PipelineIcon,
        adminOnly: true,
      },
      {
        type: "link",
        labelKey: "nav.campaigns",
        href: "/dashboard/campanhas",
        icon: LeadsIcon,
        adminOnly: true,
      },
      {
        type: "link",
        labelKey: "nav.baixas",
        href: "/dashboard/emprestimos/baixas",
        icon: ActiveLoansIcon,
        adminOnly: true,
      },
    ],
  },
  {
    type: "link",
    labelKey: "nav.clients",
    href: "/dashboard/clientes",
    icon: ClientsIcon,
    permission: "clientes:listar",
  },
  {
    type: "link",
    labelKey: "nav.leads",
    href: "/dashboard/leads",
    icon: LeadsIcon,
    adminOnly: true,
  },
  {
    type: "group",
    id: "team",
    labelKey: "nav.team",
    icon: UsersIcon,
    children: [
      {
        type: "link",
        labelKey: "nav.users",
        href: "/dashboard/usuarios",
        icon: UsersIcon,
        permission: "users.view",
      },
      {
        type: "link",
        labelKey: "nav.roles",
        href: "/dashboard/perfis",
        icon: ShieldIcon,
        permission: "roles.view",
      },
    ],
  },
  {
    type: "group",
    id: "settings",
    labelKey: "nav.settings",
    icon: SettingsIcon,
    adminOnly: true,
    children: [
      {
        type: "link",
        labelKey: "nav.settings_company",
        href: "/dashboard/configuracoes/empresa",
        icon: SettingsIcon,
        adminOnly: true,
      },
      {
        type: "link",
        labelKey: "nav.settings_theme",
        href: "/dashboard/configuracoes/tema",
        icon: SettingsIcon,
        adminOnly: true,
      },
      // Emprestimos, Renovacao e Feriados removidos — todos sao per-campanha
      // no wizard de plano de cobranca (Step2Rules, Step6Documents).
      {
        type: "link",
        labelKey: "nav.integrations",
        href: "/dashboard/configuracoes/integracoes",
        icon: IntegrationsIcon,
        adminOnly: true,
      },
    ],
  },
  {
    type: "link",
    labelKey: "nav.passkeys",
    href: "/dashboard/chaves",
    icon: KeyIcon,
  },
  {
    type: "group",
    id: "traceability",
    labelKey: "nav.traceability",
    icon: AuditIcon,
    adminOnly: true,
    children: [
      {
        type: "link",
        labelKey: "nav.audit",
        href: "/dashboard/auditoria",
        icon: AuditIcon,
        adminOnly: true,
      },
    ],
  },
  {
    type: "link",
    labelKey: "nav.billing",
    href: "/gestor",
    icon: BillingIcon,
    permission: "_master_only_",
  },
];

function flattenLinks(items: MenuItem[]): MenuLink[] {
  const out: MenuLink[] = [];
  for (const item of items) {
    if (item.type === "link") out.push(item);
    else out.push(...item.children);
  }
  return out;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const { branding } = useTenantTheme();
  const { t } = useTranslation();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Breadcrumb
  const ROUTE_LABELS: Record<string, string> = {
    "/dashboard": t("layout.breadcrumb_dashboard"),
    "/dashboard/usuarios": t("nav.users"),
    "/dashboard/perfis": t("nav.roles"),
    "/dashboard/clientes": t("nav.clients"),
    "/dashboard/emprestimos": t("nav.loans"),
    "/dashboard/emprestimos/baixas": t("nav.baixas"),
    "/dashboard/perfil": t("layout.breadcrumb_profile"),
    "/dashboard/configuracoes/empresa": t("layout.breadcrumb_company"),
    "/dashboard/configuracoes/tema": t("layout.breadcrumb_theme"),
    "/dashboard/configuracoes/integracoes": t("layout.breadcrumb_integrations"),
    "/dashboard/onboarding": t("layout.breadcrumb_onboarding"),
    "/dashboard/leads": t("layout.breadcrumb_leads"),
    "/dashboard/auditoria": t("layout.breadcrumb_audit"),
    "/dashboard/campanhas": t("layout.breadcrumb_campaigns"),
    "/dashboard/documentos": t("layout.breadcrumb_documents"),
    "/dashboard/pipeline": t("layout.breadcrumb_pipeline"),
    "/dashboard/chaves": t("passkeys.title"),
  };

  // Mapa href -> label do grupo pai (ex: /dashboard/pipeline -> "Empréstimos").
  // Serve pra injetar o grupo no breadcrumb ja que o path do URL nao carrega
  // essa informacao ("empréstimos" nao aparece na URL de campanhas/pipeline/etc).
  const parentGroupByHref: Record<string, string> = {};
  for (const item of menuItems) {
    if (item.type === "group") {
      for (const child of item.children) {
        parentGroupByHref[child.href] = t(item.labelKey);
      }
    }
  }

  const buildBreadcrumbs = (): BreadcrumbItem[] => {
    const path = location.pathname;
    // Painel de Controle nao e "pasta pai" das outras paginas — e uma pagina
    // como qualquer outra. So aparece no breadcrumb quando estamos nela.
    if (path === "/dashboard") {
      return [{ label: t("layout.breadcrumb_dashboard") }];
    }

    const crumbs: BreadcrumbItem[] = [];

    // Match direto (rota conhecida)
    if (ROUTE_LABELS[path]) {
      const parentGroup = parentGroupByHref[path];
      if (parentGroup) crumbs.push({ label: parentGroup });
      crumbs.push({ label: ROUTE_LABELS[path] });
      return crumbs;
    }

    // Rotas aninhadas (ex: /dashboard/emprestimos/:id)
    const segments = path.replace("/dashboard/", "").split("/");
    let built = "/dashboard";
    let parentInserted = false;
    for (const seg of segments) {
      built += `/${seg}`;
      const label = ROUTE_LABELS[built];
      if (label && !parentInserted) {
        const parentGroup = parentGroupByHref[built];
        if (parentGroup) crumbs.push({ label: parentGroup });
        parentInserted = true;
      }
      if (label) {
        crumbs.push({ label, href: built });
      } else if (seg.match(/^[a-f0-9-]+$/i)) {
        crumbs.push({ label: t("layout.breadcrumb_detail") });
      } else {
        crumbs.push({ label: seg.charAt(0).toUpperCase() + seg.slice(1) });
      }
    }
    return crumbs;
  };

  const isLinkVisible = (link: MenuLink): boolean => {
    if (link.permission === "_master_only_") return user?.nivel === "master";
    if (link.adminOnly) return user?.nivel === "master" || user?.nivel === "admin";
    return !link.permission || hasPermission(link.permission);
  };

  // Command palette items (sempre flat — sem grupos)
  const commandItems: CommandItem[] = [
    ...flattenLinks(menuItems)
      .filter(isLinkVisible)
      .map((item) => ({
        id: item.href,
        label: t(item.labelKey),
        group: "Paginas",
        icon: <item.icon />,
        onSelect: () => navigate(item.href),
      })),
    {
      id: "profile",
      label: t("layout.command_profile"),
      group: t("common.actions"),
      onSelect: () => navigate("/dashboard/perfil"),
    },
    {
      id: "logout",
      label: t("layout.command_logout"),
      group: t("common.actions"),
      onSelect: handleLogout,
    },
  ];

  // Placeholder notifications (will be replaced by real API)
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const handleReadNotification = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleReadAllNotifications = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const visibleMenuItems: MenuItem[] = menuItems
    .map((item) => {
      if (item.type === "link") {
        return isLinkVisible(item) ? item : null;
      }
      const visibleChildren = item.children.filter(isLinkVisible);
      if (visibleChildren.length === 0) return null;
      const groupAdminOk =
        !item.adminOnly || user?.nivel === "master" || user?.nivel === "admin";
      if (!groupAdminOk) return null;
      return { ...item, children: visibleChildren };
    })
    .filter((x): x is MenuItem => x !== null);

  const groupContainsActive = (group: MenuGroup): boolean =>
    group.children.some((c) => location.pathname.startsWith(c.href));

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const item of menuItems) {
      if (item.type === "group") init[item.id] = false;
    }
    return init;
  });

  // Auto-expande o grupo cuja rota filha estiver ativa
  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of menuItems) {
        if (item.type === "group" && groupContainsActive(item) && !prev[item.id]) {
          next[item.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (id: string) =>
    setExpandedGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-50 h-screen w-64 transform border-r border-border bg-card transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 items-center gap-2 border-b border-border px-4">
          {branding?.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.nome} className="h-10 w-10 rounded-lg object-contain" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-primary">
              <span className="text-xl font-bold text-white">
                {branding?.nome?.charAt(0) || "P"}
              </span>
            </div>
          )}
          <span className="text-xl font-bold text-foreground">
            {branding?.nome || "Pillar Money"}
          </span>
        </div>

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-1">
            {visibleMenuItems.map((item) => {
              if (item.type === "link") {
                const isActive = location.pathname === item.href;
                const Icon = item.icon;
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "bg-primary-100 font-bold text-primary-700"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      <Icon />
                      {t(item.labelKey)}
                    </Link>
                  </li>
                );
              }

              const Icon = item.icon;
              const expanded = expandedGroups[item.id] ?? false;
              const hasActive = groupContainsActive(item);

              return (
                <li key={item.id}>
                  <button
                    onClick={() => toggleGroup(item.id)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                      hasActive
                        ? "text-primary-700"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <Icon />
                    <span className="flex-1 text-left">{t(item.labelKey)}</span>
                    <ChevronIcon expanded={expanded} />
                  </button>
                  {expanded && (
                    <ul className="ml-4 mt-1 space-y-1 border-l border-border pl-3">
                      {item.children.map((child) => {
                        const childActive = location.pathname === child.href;
                        return (
                          <li key={child.href}>
                            <Link
                              to={child.href}
                              onClick={() => setSidebarOpen(false)}
                              className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-colors ${
                                childActive
                                  ? "bg-primary-100 font-bold text-primary-700"
                                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
                              }`}
                            >
                              {t(child.labelKey)}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User info at bottom */}
        <div className="border-t border-border p-4">
          <Link
            to="/dashboard/perfil"
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100">
              <span className="text-sm font-bold text-primary-700">
                {user?.nome?.charAt(0) || "U"}
              </span>
            </div>
            <div className="flex-1 truncate">
              <p className="font-bold text-foreground">{user?.nome}</p>
              <p className="truncate text-xs">{user?.email}</p>
            </div>
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card px-4 lg:px-6">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
          >
            <MenuIcon />
          </button>

          {/* Search trigger */}
          <button
            onClick={() => setCmdOpen(true)}
            className="hidden items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:flex"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span>{t("common.placeholder_search")}</span>
            <kbd className="rounded border border-border bg-card px-1.5 py-0.5 text-[10px] font-medium">
              Ctrl+K
            </kbd>
          </button>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCmdOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <NotificationBell
              notifications={notifications}
              onRead={handleReadNotification}
              onReadAll={handleReadAllNotifications}
              onClick={(n) => { if (n.href) navigate(n.href); }}
            />
            <LanguageSelector />
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={handleLogout} className="hidden sm:inline-flex">
              {t("nav.logout")}
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Breadcrumb items={buildBreadcrumbs()} onNavigate={navigate} />
          {children}
        </main>
      </div>

      {/* Command Palette */}
      <CommandPalette
        open={cmdOpen}
        onOpenChange={setCmdOpen}
        items={commandItems}
      />

      {/* Forca bootstrap de passkey pra usuarios pre-migracao */}
      <PasskeyBootstrapPrompt />
    </div>
  );
}

// Icons
function HomeIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

function LoansIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ClientsIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
      />
    </svg>
  );
}

function IntegrationsIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );
}


function LeadsIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
      />
    </svg>
  );
}

function BillingIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function AuditIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}


function PipelineIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );
}

function ActiveLoansIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M6 14h4m6 0h2M5 6h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
      />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}
